import { useTranslation } from "react-i18next";
import { Plus, Trash2, X, Save, Loader2, Image as ImageIcon } from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import { API_URL } from "../../lib/config";
import { ImageCarousel } from "../ImageCarousel";
import { MarkdownText } from "../MarkdownText";
import { MarkdownHelp } from "../MarkdownHelp";

export function ProposalsSection({
  consulta, editing, options: editedOptions,
  onOptionChange, onAddOption, onRemoveOption,
  onImageUpload, onRemoveImage, uploadingIndex, fileInputRefs,
  saving, onSave, onCancel
}) {
  const { t } = useTranslation();

  return (
    <div className="bg-white border border-slate-200 rounded-lg p-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg font-heading font-semibold text-slate-900">{t('consultaDetail.proposals')}</h2>
      </div>

      {!editing && (
        <div className="space-y-4">
          {consulta.options.map((option, index) => {
            const optionTitle = typeof option === 'string' ? option : option.title;
            const optionDesc = typeof option === 'object' ? option.description : null;
            const optionImage = typeof option === 'object' ? option.image_url : null;
            const optionImages = typeof option === 'object' ? (option.image_urls?.length ? option.image_urls : (optionImage ? [optionImage] : [])) : [];
            const optionAmount = typeof option === 'object' ? option.amount : null;
            const carouselUrls = optionImages.map(u => `${API_URL}${u}`);
            return (
              <div key={index} className="border border-slate-200 rounded-lg overflow-hidden">
                {carouselUrls.length > 0 && <ImageCarousel images={carouselUrls} alt={optionTitle} maxHeight="max-h-80" />}
                <div className="p-4 flex items-start gap-3">
                  <span className="w-8 h-8 bg-slate-200 rounded-full flex items-center justify-center text-sm font-medium flex-shrink-0">{index + 1}</span>
                  <div className="flex-1">
                    <h3 className="font-medium text-slate-900">{optionTitle}</h3>
                    {optionDesc && <MarkdownText className="text-sm text-slate-500 mt-1">{optionDesc}</MarkdownText>}
                    {optionAmount && <p className="text-sm text-emerald-600 mt-2 font-medium">{t('consultaDetail.amountLabel')}: {optionAmount.toLocaleString('ca-ES', { style: 'currency', currency: 'EUR' })}</p>}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {editing && (
        <div className="space-y-6">
          {editedOptions.map((option, index) => (
            <div key={index} className="border border-slate-200 rounded-lg p-4 bg-slate-50">
              <div className="flex items-center justify-between mb-4">
                <span className="font-medium text-slate-700">{t('consultaDetail.proposalNum', { num: index + 1 })}</span>
                {editedOptions.length > 2 && (
                  <Button type="button" variant="ghost" size="sm" onClick={() => onRemoveOption(index)}
                    className="text-slate-400 hover:text-red-500" data-testid={`remove-option-${index}`}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                )}
              </div>
              <div className="mb-4">
                <Label className="text-sm text-slate-600 mb-1 block">{t('consultaDetail.titleRequired')} *</Label>
                <Input value={option.title} onChange={(e) => onOptionChange(index, 'title', e.target.value)}
                  placeholder={`Ex: Proposta ${index + 1}`} className="h-10" data-testid={`edit-option-title-${index}`} />
              </div>
              <div className="mb-4">
                <Label className="text-sm text-slate-600 mb-1 block">{t('consultaDetail.descriptionLabel')} <span className="ml-1"><MarkdownHelp /></span></Label>
                <Textarea value={option.description || ""} onChange={(e) => onOptionChange(index, 'description', e.target.value)}
                  placeholder={t('consultaDetail.descriptionPlaceholder')} rows={3} data-testid={`edit-option-description-${index}`} />
              </div>
              <div className="mb-4">
                <Label className="text-sm text-slate-600 mb-1 block">{t('consultaDetail.amountLabel')} (EUR)</Label>
                <Input type="number" step="0.01" min="0" value={option.amount || ""}
                  onChange={(e) => onOptionChange(index, 'amount', e.target.value)}
                  placeholder="Ex: 15000" className="h-10 w-48" data-testid={`edit-option-amount-${index}`} />
                <p className="text-xs text-slate-400 mt-1">{t('consultaDetail.proposalCost')}</p>
              </div>
              <div>
                <Label className="text-sm text-slate-600 mb-2 block">{t('consultaDetail.imageLabel')}</Label>
                <div className="space-y-2">
                  <div className="flex flex-wrap gap-3 pt-1 pr-1">
                    {(option.image_urls?.length ? option.image_urls : (option.image_url ? [option.image_url] : [])).map((url, imgIdx) => (
                      <div key={imgIdx} className="relative inline-block">
                        <img src={`${API_URL}${url}`} alt={`Proposta ${index + 1} - ${imgIdx + 1}`} className="h-24 w-32 object-contain rounded-lg border border-slate-200 bg-slate-50" />
                        <button type="button" onClick={() => onRemoveImage(index, imgIdx)}
                          className="absolute -top-2 -right-2 z-10 bg-red-500 text-white rounded-full p-1.5 hover:bg-red-600 shadow-md cursor-pointer" data-testid={`remove-image-${index}-${imgIdx}`}>
                          <X className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    ))}
                    {(option.image_urls?.length || (option.image_url ? 1 : 0)) < 6 && (
                      <div>
                        <input type="file" accept=".jpg,.jpeg,.png" ref={el => fileInputRefs.current[index] = el}
                          onChange={(e) => onImageUpload(index, e.target.files[0])} className="hidden" data-testid={`image-input-${index}`} />
                        <Button type="button" variant="outline" onClick={() => fileInputRefs.current[index]?.click()}
                          disabled={uploadingIndex === index} className="h-24 w-32 border-dashed flex flex-col items-center justify-center gap-1"
                          data-testid={`upload-image-btn-${index}`}>
                          {uploadingIndex === index
                            ? <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
                            : <><ImageIcon className="h-5 w-5 text-slate-400" /><span className="text-xs text-slate-500">+ {t('consultaDetail.uploadImage')}</span></>
                          }
                        </Button>
                      </div>
                    )}
                  </div>
                  {(option.image_urls?.length || (option.image_url ? 1 : 0)) > 0 && (
                    <p className="text-xs text-slate-400">{(option.image_urls?.length || (option.image_url ? 1 : 0))}/6</p>
                  )}
                </div>
              </div>
            </div>
          ))}
          <Button type="button" variant="outline" onClick={onAddOption} className="w-full" data-testid="add-option-btn">
            <Plus className="h-4 w-4 mr-2" />{t('consultaDetail.addProposal')}
          </Button>
        </div>
      )}
    </div>
  );
}
